import type { OnInit} from '@angular/core';
import { Component, ViewChild, inject, signal, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AuthService } from '@echospaces-web-monorepo/es-auth';
import {
  TreeViewComponent, type TreeNode, CardComponent, SpinnerComponent,
  EmptyStateComponent, BadgeComponent, ButtonComponent, TabSetComponent, type Tab,
  AvatarComponent, ToastService,
} from '@echospaces-web-monorepo/es-shared-ui';

import { SpacePlaceholdersTabComponent } from '../placeholders/space-placeholders-tab.component';
import { EditSpaceModalComponent } from './edit-space-modal.component';
import { MyGrantsService } from './my-grants.service';
import { SpaceMembersTabComponent } from './space-members-tab.component';
import { SpaceService, type Space, type UpdateSpaceDto } from './space.service';

const SPACE_ICONS: Record<string, string> = {
  island: '◆', hex: '⬡', building: '▣', floor: '▤', room: '▢', open_space: '◈',
};

/**
 * Renders the spatial hierarchy tree for a world island and a detail panel for the selected space.
 * Admins can browse space metadata, children, and placeholder slots.
 */
@Component({
  selector: 'es-space-tree-page',
  imports: [
    TreeViewComponent, CardComponent, SpinnerComponent, EmptyStateComponent,
    BadgeComponent, ButtonComponent, TabSetComponent, AvatarComponent,
    SpaceMembersTabComponent, SpacePlaceholdersTabComponent,
    EditSpaceModalComponent,
  ],
  template: `
    <div class="flex h-full gap-6">
      <!-- Left: Tree -->
      <div class="w-72 shrink-0 overflow-auto border-r border-[var(--border)] pr-4">
        <h3 class="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3">Spatial Tree</h3>
        @if (spaceService.loading()) {
          <es-spinner label="Loading..."></es-spinner>
        } @else {
          <es-tree-view
            [nodes]="treeNodes()"
            [selectedId]="spaceService.selectedSpace()?.id ?? null"
            (nodeSelected)="onNodeSelected($event)"
          ></es-tree-view>
        }
      </div>

      <!-- Right: Detail -->
      <div class="flex-1 overflow-auto">
        @if (spaceService.selectedSpace(); as space) {
          <div class="max-w-3xl">
            <!-- Header -->
            <div class="flex items-center justify-between mb-6">
              <div>
                <div class="flex items-center gap-2">
                  <span class="text-2xl">{{ SPACE_ICONS[space.spaceType] }}</span>
                  <h2 class="text-2xl font-bold">{{ space.name }}</h2>
                </div>
                <div class="flex items-center gap-2 mt-1">
                  <es-badge [label]="space.spaceType" variant="secondary"></es-badge>
                  <es-badge [label]="space.slug" variant="primary"></es-badge>
                </div>
              </div>
              @if (canEditMetadata()) {
                <es-button variant="secondary" size="sm" (clicked)="openEditModal()">Edit</es-button>
              }
            </div>

            <!-- Tabs -->
            <es-tab-set [tabs]="detailTabs()" (tabChanged)="activeTab.set($event)">
              @switch (effectiveTab()) {
                @case ('info') {
                  <es-card>
                    <div class="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span class="text-[var(--text-secondary)]">Slug</span>
                        <p class="font-medium">{{ space.slug }}</p>
                      </div>
                      <div>
                        <span class="text-[var(--text-secondary)]">Type</span>
                        <p class="font-medium capitalize">{{ space.spaceType }}</p>
                      </div>
                      <div>
                        <span class="text-[var(--text-secondary)]">Description</span>
                        <p class="font-medium">{{ space.description || '—' }}</p>
                      </div>
                      <div>
                        <span class="text-[var(--text-secondary)]">Order</span>
                        <p class="font-medium">{{ space.order }}</p>
                      </div>
                    </div>
                  </es-card>
                }
                @case ('children') {
                  @if (space.children.length === 0) {
                    <es-empty-state
                      title="No children"
                      description="This space has no sub-spaces."
                      icon="◫"
                    ></es-empty-state>
                  } @else {
                    <div class="grid grid-cols-2 gap-3">
                      @for (child of space.children; track child.id) {
                        <es-card [clickable]="true" (clicked)="selectChild(child)">
                          <div class="flex items-center gap-3">
                            <span class="text-xl">{{ SPACE_ICONS[child.spaceType] }}</span>
                            <div>
                              <p class="font-medium">{{ child.name }}</p>
                              <p class="text-xs text-[var(--text-secondary)] capitalize">{{ child.spaceType }}</p>
                            </div>
                          </div>
                        </es-card>
                      }
                    </div>
                  }
                }
                @case ('placeholders') {
                  <es-space-placeholders-tab
                    [spaceId]="space.id"
                    [islandSpaceId]="islandSpaceId() ?? space.id"
                    [canEdit]="canEditSelected()"
                  ></es-space-placeholders-tab>
                }
                @case ('members') {
                  <es-space-members-tab
                    [spaceId]="space.id"
                    [canManage]="canManageSelected()"
                  ></es-space-members-tab>
                }
              }
            </es-tab-set>
          </div>
        } @else {
          <es-empty-state
            title="Select a space"
            description="Choose a space from the tree to view details."
            icon="◫"
          ></es-empty-state>
        }
      </div>

      <es-edit-space-modal
        #editModal
        [open]="editOpen()"
        [space]="spaceService.selectedSpace()"
        (closed)="editOpen.set(false)"
        (submitted)="onEditSubmit($event)"
      ></es-edit-space-modal>
    </div>
  `,
})
export class SpaceTreePageComponent implements OnInit {
  @ViewChild('editModal') editModalRef?: EditSpaceModalComponent;

  spaceService = inject(SpaceService);
  grantsService = inject(MyGrantsService);
  private route = inject(ActivatedRoute);
  private auth = inject(AuthService);
  private toast = inject(ToastService);

  SPACE_ICONS = SPACE_ICONS;
  activeTab = signal('info');

  /** Island id from the route — used to scope the placeholder-link list + content library. */
  readonly islandSpaceId = signal<string | null>(null);

  /** Edit-Space modal open state. */
  readonly editOpen = signal(false);

  /**
   * Metadata-edit visibility: org Super-Admin only (matches what the
   * backend's `update Space` CASL gate accepts in portal context).
   * Per-space ADMIN delegates need a backend extension first.
   */
  readonly canEditMetadata = computed(() =>
    this.auth.user()?.roles.includes('super-admin') ?? false,
  );

  private readonly baseTabs: Tab[] = [
    { id: 'info', label: 'Info', icon: 'ℹ' },
    { id: 'children', label: 'Children', icon: '◫' },
    { id: 'placeholders', label: 'Placeholders', icon: '📌' },
  ];
  private readonly membersTab: Tab = { id: 'members', label: 'Members', icon: '👥' };

  // Members tab is gated behind `canEdit` — visitors don't see it at all,
  // editors see it and can interact with the row controls when canManage
  // is additionally true (handled inside SpaceMembersTabComponent).
  detailTabs = computed<Tab[]>(() => {
    const tabs = [...this.baseTabs];
    if (this.canEditSelected()) tabs.push(this.membersTab);
    return tabs;
  });

  treeNodes = computed<TreeNode[]>(() =>
    this.spaceService.spaces().map(s => this.spaceToNode(s)));

  // Flat parent lookup built from the loaded tree — feeds the grant
  // closure walk so we can ask `canManageAt(space.id)` without extra
  // round-trips.
  private readonly parentMap = computed<Map<string, string | null>>(() => {
    const map = new Map<string, string | null>();
    const visit = (space: Space, parentId: string | null) => {
      map.set(space.id, parentId);
      space.children?.forEach(child => visit(child, space.id));
    };
    this.spaceService.spaces().forEach(root => visit(root, null));
    return map;
  });

  canManageSelected = computed(() => {
    const space = this.spaceService.selectedSpace();
    if (!space) return false;
    const map = this.parentMap();
    return this.grantsService.canManageAt(space.id, id => map.get(id) ?? null);
  });

  canEditSelected = computed(() => {
    const space = this.spaceService.selectedSpace();
    if (!space) return false;
    const map = this.parentMap();
    return this.grantsService.canEditAt(space.id, id => map.get(id) ?? null);
  });

  /**
   * Falls back to `info` when the active tab is no longer in `detailTabs`
   * (e.g. user was on `members` then navigated to a space they can't
   * edit). Avoids the empty-render glitch from a stale @switch value.
   */
  effectiveTab = computed(() => {
    const active = this.activeTab();
    return this.detailTabs().some(t => t.id === active) ? active : 'info';
  });

  ngOnInit() {
    const islandId = this.route.parent?.snapshot.params['islandId'];
    if (islandId) {
      this.islandSpaceId.set(islandId);
      this.spaceService.loadSpaces(islandId);
    }
    if (!this.grantsService.loaded()) {
      this.grantsService.loadGrants();
    }
  }

  onNodeSelected(node: TreeNode) {
    this.spaceService.loadSpace(node.id);
  }

  selectChild(child: Space) {
    this.spaceService.loadSpace(child.id);
  }

  openEditModal(): void {
    if (this.canEditMetadata()) this.editOpen.set(true);
  }

  async onEditSubmit(dto: UpdateSpaceDto): Promise<void> {
    const space = this.spaceService.selectedSpace();
    if (!space) return;
    try {
      await this.spaceService.updateSpace(space.id, dto);
      this.editModalRef?.finishSubmit();
      this.toast.success('Space updated');
      this.editOpen.set(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update space';
      this.editModalRef?.finishSubmit(message);
    }
  }

  private spaceToNode(space: Space): TreeNode {
    return {
      id: space.id,
      label: space.name,
      icon: SPACE_ICONS[space.spaceType],
      children: space.children?.map(c => this.spaceToNode(c)),
      data: space,
    };
  }
}
